import React, { useEffect, useMemo, useRef, useState } from "react";
import Slider from "react-slick";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as publicApi from "../api/publicApi";
import { DAY_ORDER } from "../utils/classTimeUtils";
import TeacherPhoto from "./TeacherPhoto";
import "./HomeCarousel.css";

function parseScheduleGroups(schedule = []) {
  if (!Array.isArray(schedule) || schedule.length === 0) return [];
  return schedule
    .filter(
      (group) =>
        group &&
        Array.isArray(group.days) &&
        group.days.length > 0 &&
        group.startTime &&
        group.endTime,
    )
    .map((group) => ({
      days: [...group.days].sort(
        (a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b),
      ),
      startTime: group.startTime,
      endTime: group.endTime,
    }));
}

function ScheduleArrow({ direction, label, onClick, disabled }) {
  return (
    <button
      type="button"
      className={`home-schedule-arrow home-schedule-arrow--${direction}`}
      onClick={onClick}
      aria-label={label}
      disabled={disabled}
    >
      <FontAwesomeIcon
        icon={direction === "prev" ? faChevronLeft : faChevronRight}
        aria-hidden="true"
      />
    </button>
  );
}

function sessionMeta(course) {
  const start = course.Session?.startMonth;
  const end = course.Session?.endMonth;
  const year = course.year;
  const range = start && end ? `${start} – ${end}` : null;
  return { range, year: year ? String(year) : null };
}

function groupCoursesByTeacher(teachers = [], courses = []) {
  const map = new Map();

  for (const teacher of teachers) {
    if (!teacher?.id) continue;
    map.set(Number(teacher.id), {
      id: Number(teacher.id),
      name: teacher.name || "Teacher",
      picture: teacher.picture || null,
      courses: [],
    });
  }

  for (const course of courses) {
    const teacherId = Number(course.teacherId);
    if (!Number.isInteger(teacherId) || teacherId <= 0) continue;

    if (!map.has(teacherId)) {
      map.set(teacherId, {
        id: teacherId,
        name: course.Teacher?.name || "Teacher",
        picture: course.Teacher?.picture || null,
        courses: [],
      });
    }

    map.get(teacherId).courses.push(course);
  }

  return [...map.values()].filter((entry) => entry.courses.length > 0);
}

function coursesDensityClass(count) {
  if (count >= 5) return "home-schedule-slide__courses--dense";
  if (count >= 3) return "home-schedule-slide__courses--split";
  return "";
}

function TeacherScheduleSlide({ teacher }) {
  const courseCount = teacher.courses.length;
  const coursesClass = [
    "home-schedule-slide__courses",
    coursesDensityClass(courseCount),
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="home-schedule-slide">
      <article className="home-schedule-slide__panel">
        <header className="home-schedule-slide__header">
          <TeacherPhoto
            className="home-schedule-slide__photo"
            picture={teacher.picture}
            name={teacher.name}
            variant="circle"
          />
          <div className="home-schedule-slide__identity">
            <h3 className="home-schedule-slide__name">{teacher.name}</h3>
          </div>
        </header>

        <div className={coursesClass}>
          {teacher.courses.map((course) => {
            const { range, year } = sessionMeta(course);
            const slots = parseScheduleGroups(course.classTime);
            return (
              <div key={course.id} className="home-schedule-course">
                <div className="home-schedule-course__main">
                  <h4 className="home-schedule-course__title">{course.title}</h4>
                  {(range || year) && (
                    <div className="home-schedule-course__meta">
                      {range ? (
                        <span className="home-schedule-course__chip">
                          {range}
                        </span>
                      ) : null}
                      {year ? (
                        <span className="home-schedule-course__chip home-schedule-course__chip--year">
                          {year}
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>

                <div className="home-schedule-course__schedule">
                  {slots.length === 0 ? (
                    <span className="home-schedule-course__time-empty">
                      Not Set
                    </span>
                  ) : (
                    slots.map((slot, index) => (
                      <div
                        key={`${course.id}-slot-${index}`}
                        className="home-schedule-course__slot"
                      >
                        <div className="home-schedule-course__days">
                          {slot.days.map((day) => (
                            <span
                              key={day}
                              className="home-schedule-course__day"
                            >
                              {day}
                            </span>
                          ))}
                        </div>
                        <span className="home-schedule-course__clock">
                          <i
                            className="fa-solid fa-clock"
                            aria-hidden="true"
                          />
                          {slot.startTime} – {slot.endTime}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </article>
    </div>
  );
}

/**
 * Dynamic home carousel: multiple teacher schedule cards visible at once.
 */
export default function HomeCarousel() {
  const [teachers, setTeachers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [teachersRes, coursesRes] = await Promise.all([
          publicApi.getTeachers(),
          publicApi.getActiveCourses(),
        ]);
        if (cancelled) return;
        setTeachers(Array.isArray(teachersRes.data) ? teachersRes.data : []);
        setCourses(Array.isArray(coursesRes.data) ? coursesRes.data : []);
      } catch (err) {
        console.error("Failed to load schedule carousel:", err);
        if (!cancelled) {
          setTeachers([]);
          setCourses([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const slides = useMemo(
    () => groupCoursesByTeacher(teachers, courses),
    [teachers, courses],
  );
  const sliderRef = useRef(null);

  if (loading) {
    return (
      <section className="student-section student-schedule" aria-label="Class schedules">
        <div className="student-section__inner">
          <p className="home-schedule-carousel__loading">
            Loading class schedules…
          </p>
        </div>
      </section>
    );
  }

  if (slides.length === 0) {
    return null;
  }

  const canScroll = slides.length > 1;
  const goPrev = () => sliderRef.current?.slickPrev();
  const goNext = () => sliderRef.current?.slickNext();

  const controls = {
    arrows: false,
    dots: canScroll,
    // react-slick replaces appendDots className with "slick-dots" + dotsClass
    dotsClass: "home-schedule-controls",
    appendDots: (dots) => (
      <div role="group" aria-label="Schedule navigation">
        <ScheduleArrow
          direction="prev"
          label="Previous class schedules"
          onClick={goPrev}
        />
        <ul className="home-schedule-dots">{dots}</ul>
        <ScheduleArrow
          direction="next"
          label="Next class schedules"
          onClick={goNext}
        />
      </div>
    ),
    customPaging: () => <span className="home-schedule-dot" />,
  };

  const settings = {
    ...controls,
    infinite: slides.length > 3,
    speed: 650,
    cssEase: "ease-in-out",
    slidesToShow: Math.min(3, slides.length),
    slidesToScroll: 1,
    autoplay: canScroll,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    swipeToSlide: true,
    responsive: [
      {
        breakpoint: 1100,
        settings: {
          ...controls,
          slidesToShow: Math.min(2, slides.length),
          slidesToScroll: 1,
          infinite: slides.length > 2,
        },
      },
      {
        breakpoint: 700,
        settings: {
          ...controls,
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: slides.length > 1,
        },
      },
    ],
  };

  return (
    <section className="student-section student-schedule" aria-label="Class schedules">
      <div className="student-section__inner">
        <header className="student-section__header">
          <p className="student-section__eyebrow">Timetable</p>
          <h2 className="student-section__title">Class schedules</h2>
          <p className="student-section__lede">
            Browse active classes by teacher and find a time that fits.
          </p>
        </header>
      </div>
      <div className="student-schedule__track home-schedule-carousel">
        <Slider ref={sliderRef} {...settings}>
          {slides.map((teacher) => (
            <TeacherScheduleSlide key={teacher.id} teacher={teacher} />
          ))}
        </Slider>
      </div>
    </section>
  );
}
